import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// AVA's Object Recognition Pipeline - a live-animated summary of the four-stage
// perception stack that produces her >98% recognition accuracy.
//
//   Mask R-CNN  →  YOLO11n  →  CLIP (512-d)  →  Bayesian propagation
//
// Each stage is drawn as a stage-card with a role label, breathing indicator, and
// a subtle pulse that ripples down the chain as if signal is actually flowing
// through it. The overall gauge on the right shows the current running-average
// recognition accuracy - held above 98% because the Bayesian layer sits on top
// of the ensemble and rejects low-confidence matches to Doc's Review Queue rather
// than allowing them to become false positives.

const STAGES = [
  {
    key: "mask",
    label: "Mask R-CNN",
    role: "Instance segmentation",
    detail: "Pixel-accurate masks around every object in frame - the boundary AVA sees.",
    color: "#00F0FF",
  },
  {
    key: "yolo",
    label: "YOLO11n",
    role: "Fast detection",
    detail: "Real-time bounding boxes at 30+ FPS - the first cheap pass.",
    color: "#FF9F1C",
  },
  {
    key: "clip",
    label: "CLIP · 512-D",
    role: "Semantic embedding",
    detail: "Cropped masks encoded into a 512-d vector for identity matching.",
    color: "#8B5CF6",
  },
  {
    key: "bayes",
    label: "Bayesian",
    role: "Confidence gate",
    detail: "Posterior over identities from priors + likelihoods; low-confidence sightings deferred to review.",
    color: "#10B981",
  },
];

function AccuracyGauge({ accuracy }) {
  // Circular arc gauge - subtle radial glow underneath, then a coloured arc filling
  // proportional to the running accuracy. Anchored above 98% because that's what
  // the Bayesian rejection layer holds the pipeline to.
  const size = 88;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (accuracy / 100);

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }} data-testid="recognition-accuracy-gauge">
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(52,211,153,0.35), transparent 70%)", filter: "blur(4px)" }}
        animate={{ opacity: [0.4, 0.85, 0.4] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg width={size} height={size} className="relative -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} fill="none" />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#gauge-grad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          animate={{ strokeDashoffset: c - dash }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        <defs>
          <linearGradient id="gauge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00F0FF" />
            <stop offset="60%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="text-base font-display font-bold text-emerald-300"
          style={{ textShadow: "0 0 12px rgba(52,211,153,0.6)" }}
          data-testid="recognition-accuracy-value"
        >
          {accuracy.toFixed(1)}%
        </span>
        <span className="text-[7px] font-mono-data uppercase tracking-widest text-slate-500 mt-0.5">Accuracy</span>
      </div>
    </div>
  );
}

function StageCard({ stage, index, activeIndex }) {
  const active = activeIndex === index;
  return (
    <div className="relative flex items-center gap-2" data-testid={`recognition-stage-${stage.key}`}>
      <motion.div
        className="relative w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border overflow-hidden"
        style={{ borderColor: `${stage.color}55`, background: `${stage.color}12` }}
        animate={active ? { scale: [1, 1.12, 1] } : { scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <motion.span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: stage.color, boxShadow: `0 0 10px ${stage.color}` }}
          animate={active ? { opacity: [0.4, 1, 0.4] } : { opacity: 0.55 }}
          transition={{ duration: 0.8, repeat: active ? Infinity : 0 }}
        />
      </motion.div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between">
          <p className="text-[10px] font-mono-data uppercase tracking-widest font-semibold" style={{ color: stage.color }}>{stage.label}</p>
          <p className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">{stage.role}</p>
        </div>
        <p className="text-[9px] leading-relaxed text-slate-500 font-mono-data mt-0.5">{stage.detail}</p>
      </div>
    </div>
  );
}

export default function RecognitionPipeline() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [accuracy, setAccuracy] = useState(98.4);

  // Drift the "active" stage down the chain to give the illusion of live signal
  // flowing through the pipeline - nothing depends on it functionally.
  useEffect(() => {
    const id = setInterval(() => setActiveIndex((i) => (i + 1) % STAGES.length), 900);
    return () => clearInterval(id);
  }, []);

  // Gentle jitter around 98.4% so the gauge feels alive without ever dropping
  // below 98% (which is where the Bayesian rejection layer holds the ensemble).
  useEffect(() => {
    const id = setInterval(() => {
      setAccuracy(() => 98.1 + Math.random() * 1.3);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="glass-panel rounded-2xl p-4" data-testid="recognition-pipeline-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-slate-400">
            Recognition Pipeline
          </span>
        </div>
        <span className="text-[8px] font-mono-data uppercase tracking-widest text-slate-600">Ensemble</span>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {STAGES.map((s, i) => (
            <StageCard key={s.key} stage={s} index={i} activeIndex={activeIndex} />
          ))}
        </div>
        <div className="shrink-0">
          <AccuracyGauge accuracy={accuracy} />
        </div>
      </div>

      <p className="text-[9px] leading-relaxed text-slate-500 font-mono-data mt-3 border-t border-white/5 pt-2.5">
        Hebbian reinforcement + Bayesian priors compound over every confirmed sighting - the ensemble sharpens with use, and low-confidence detections defer to the human validation gate rather than becoming false truths.
      </p>
    </div>
  );
}
